import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Shield, UserMinus, UserPlus, Users } from "lucide-react";
import { useLayout } from "@/contexts/LayoutContext";
import { useAuth } from "@/contexts/AuthContext";
import { useIsSystemAdmin } from "@/hooks/useIsSystemAdmin";
import { useRemoveTenantMember } from "@/hooks/useRemoveTenantMember";
import { useRemoveTenantAdmin } from "@/hooks/useRemoveTenantAdmin";
import { useTenant } from "@/hooks/useTenant";
import { useTenantAdmins, type TenantAdmin } from "@/hooks/useTenantAdmins";
import { LoadingSpinner } from "@/components/loading/LoadingSpinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/button";
import { toast } from "@/utils/toast";
import { Route } from "@/routes/admin/tenants/$tenantId";
import { AddMembersDialog } from "@/components/admin/AddMembersDialog";
import { RemoveMemberDialog } from "@/components/admin/RemoveMemberDialog";
import { AddAdminDialog } from "@/components/admin/AddAdminDialog";
import { RemoveAdminDialog } from "@/components/admin/RemoveAdminDialog";

export function TenantDetailPage() {
  const { t } = useTranslation(["admin"]);
  const { navbarHeightRem } = useLayout();
  const { authUser } = useAuth();
  const navigate = useNavigate();
  const { tenantId } = Route.useParams();
  const { isSystemAdmin, isLoading: isAdminLoading } = useIsSystemAdmin();
  const {
    tenant,
    isLoading: isTenantLoading,
    error: tenantError,
  } = useTenant(tenantId);
  const hasRedirected = useRef(false);
  const [isAddMembersOpen, setIsAddMembersOpen] = useState(false);
  const [removeMemberEmail, setRemoveMemberEmail] = useState<string | null>(
    null,
  );
  const [isAddAdminOpen, setIsAddAdminOpen] = useState(false);
  const [removeAdminTarget, setRemoveAdminTarget] =
    useState<TenantAdmin | null>(null);
  const { removeMember, isSubmitting: isRemovingMember } =
    useRemoveTenantMember(tenantId, tenant?.name ?? "");
  const { removeAdmin, isSubmitting: isRemovingAdmin } = useRemoveTenantAdmin(
    tenantId,
    tenant?.name ?? "",
  );
  const { admins, isLoading: isLoadingAdmins } = useTenantAdmins(
    isSystemAdmin && tenant ? tenant.adminUids : [],
  );

  const isLoading = isAdminLoading || isTenantLoading;

  useEffect(() => {
    if (isLoading || hasRedirected.current || !authUser) return;

    if (tenantError) {
      hasRedirected.current = true;
      const isPermissionDenied =
        "code" in tenantError && tenantError.code === "permission-denied";

      if (isPermissionDenied) {
        toast.error(t("admin:tenants.unauthorizedAccess"));
        navigate({ to: "/admin/tenants" });
      } else {
        toast.error(t("admin:tenants.errorLoadingTenant"));
        navigate({ to: "/admin/tenants" });
      }
      return;
    }

    if (!tenant) {
      hasRedirected.current = true;
      toast.error(t("admin:tenants.tenantNotFound"));
      navigate({ to: "/admin/tenants" });
      return;
    }

    if (!isSystemAdmin && !tenant.adminUids.includes(authUser?.uid ?? "")) {
      hasRedirected.current = true;
      toast.error(t("admin:tenants.unauthorizedAccess"));
      navigate({
        to: "/home",
        search: { redirect: undefined, downloadJobId: undefined },
      });
      return;
    }
  }, [isLoading, tenant, tenantError, isSystemAdmin, authUser, navigate, t]);

  const isAuthorized =
    tenant && (isSystemAdmin || tenant.adminUids.includes(authUser?.uid ?? ""));

  if (isLoading || tenantError || !tenant || !isAuthorized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="64px" />
      </div>
    );
  }

  const isTenantAdmin = tenant.adminUids.includes(authUser?.uid ?? "");
  const canManageMembers =
    tenant.status === "ACTIVE" && (isSystemAdmin || isTenantAdmin);
  const canManageAdmins = tenant.status === "ACTIVE" && isSystemAdmin;

  return (
    <div
      className="flex w-full flex-col gap-6"
      style={{ paddingTop: `${navbarHeightRem + 1}rem` }}
    >
      <Link
        to="/admin/tenants"
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("admin:tenants.detail.backToList")}
      </Link>

      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">{tenant.name}</h1>
        <Badge variant={tenant.status === "ACTIVE" ? "default" : "error"}>
          {tenant.status}
        </Badge>
      </div>

      {isSystemAdmin && (
        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-lg font-medium">
                {t("admin:tenants.detail.administrators")}
              </h2>
            </div>
            {canManageAdmins && (
              <Button
                variant="default"
                size="sm"
                icon={<UserPlus className="h-4 w-4" />}
                onClick={() => setIsAddAdminOpen(true)}
                testId="add-admin-button"
              >
                {t("admin:tenants.detail.addAdmin")}
              </Button>
            )}
          </div>
          {isLoadingAdmins ? (
            <p className="pl-6 text-sm text-muted-foreground">
              {t("admin:tenants.detail.loadingAdministrators")}
            </p>
          ) : (
            <ul className="flex flex-col gap-2 pl-6">
              {admins.map((admin) => (
                <li
                  key={admin.uid}
                  className="flex items-center justify-between text-sm text-muted-foreground"
                >
                  <span>{admin.email || admin.uid}</span>
                  {canManageAdmins && (
                    <Button
                      variant="ghost"
                      size="xs"
                      icon={<UserMinus className="h-3.5 w-3.5" />}
                      onClick={() => setRemoveAdminTarget(admin)}
                      testId={`remove-admin-${admin.uid}`}
                      aria-label={t(
                        "admin:tenants.detail.removeAdminAriaLabel",
                        { email: admin.email || admin.uid },
                      )}
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    >
                      {t("admin:tenants.detail.removeAdmin")}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            <Users className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-lg font-medium">
              {t("admin:tenants.detail.members")}
            </h2>
          </div>
          {canManageMembers && (
            <Button
              variant="default"
              size="sm"
              icon={<UserPlus className="h-4 w-4" />}
              onClick={() => setIsAddMembersOpen(true)}
              testId="add-members-button"
            >
              {t("admin:tenants.detail.addMembers")}
            </Button>
          )}
        </div>
        {tenant.activeUserEmails.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
            <Users className="h-12 w-12" aria-hidden="true" />
            <p className="text-lg">{t("admin:tenants.detail.noMembers")}</p>
            <p className="text-sm">
              {t("admin:tenants.detail.noMembersDescription")}
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-2 pl-6">
            {tenant.activeUserEmails.map((email) => (
              <li
                key={email}
                className="flex items-center justify-between text-sm text-muted-foreground"
              >
                <span>{email}</span>
                {canManageMembers && (
                  <Button
                    variant="ghost"
                    size="xs"
                    icon={<UserMinus className="h-3.5 w-3.5" />}
                    onClick={() => setRemoveMemberEmail(email)}
                    testId={`remove-member-${email.replace(/[^a-z0-9]/gi, "-")}`}
                    aria-label={t(
                      "admin:tenants.detail.removeMemberAriaLabel",
                      { email },
                    )}
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    {t("admin:tenants.detail.removeMember")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {canManageMembers && (
        <>
          <AddMembersDialog
            open={isAddMembersOpen}
            onOpenChange={setIsAddMembersOpen}
            tenantId={tenant.tenantId}
            existingEmails={tenant.activeUserEmails}
          />
          <RemoveMemberDialog
            open={removeMemberEmail !== null}
            onOpenChange={(open) => {
              if (!open) setRemoveMemberEmail(null);
            }}
            tenantName={tenant.name}
            email={removeMemberEmail ?? ""}
            removeMember={removeMember}
            isSubmitting={isRemovingMember}
          />
        </>
      )}

      {canManageAdmins && (
        <>
          <AddAdminDialog
            open={isAddAdminOpen}
            onOpenChange={setIsAddAdminOpen}
            tenantId={tenant.tenantId}
            tenantName={tenant.name}
            existingAdminUids={tenant.adminUids}
          />
          <RemoveAdminDialog
            open={removeAdminTarget !== null}
            onOpenChange={(open) => {
              if (!open) setRemoveAdminTarget(null);
            }}
            tenantName={tenant.name}
            uid={removeAdminTarget?.uid ?? ""}
            email={removeAdminTarget?.email ?? ""}
            removeAdmin={removeAdmin}
            isSubmitting={isRemovingAdmin}
          />
        </>
      )}
    </div>
  );
}
